import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";
import { PATH } from "../../shared/constants";
import { buildDownloadGameSegment } from "../../utils/downloadS3Path";
import { sanitizeDownloadSeatToken } from "../../utils/downloadSeatTokens";

export interface LogActionRequest {
  userId: string;
  imageUrl: string;
  action: "download" | "share";
  stadium: string;
  section?: string;
  row?: string;
  seat?: string;
  date?: string;
}

export interface LogActionResponse {
  success: boolean;
  s3Key: string;
}

export const logAction = async (
  request: LogActionRequest,
): Promise<LogActionResponse> => {
  try {
    const { userId, imageUrl, action, stadium, section, row, seat, date } = request;
    const response = await instance.post<LogActionResponse>(`${PATH.API}/${PATH.IMAGE}/${PATH.LOG_ACTION}`, {
      userId,
      imageUrl,
      action,
      stadium,
      section,
      row,
      seat,
      date,
    });
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

interface GetPreSignedUploadRequest {
  s3Key: string;
  contentType: string;
}

interface GetPreSignedUploadResponse {
  url: string;
  fields: any;
}

const getPreSignedUpload = async ({
  s3Key,
  contentType,
}: GetPreSignedUploadRequest): Promise<GetPreSignedUploadResponse> => {
  console.log("Getting pre-signed upload for S3 key:", s3Key);
  const response = await instance.post<GetPreSignedUploadResponse>(
    `${PATH.API}/${PATH.IMAGE}/${PATH.PRE_SIGNED_UPLOAD}`, {
      contentType,
      s3Key,
    },
  );
  return response.data;
};

// Updated interface to include gameName
export interface UploadImageRequest {
  imageUrl: string;
  action: "download" | "share" | "videoboard";
  stadium: string | { name?: string };
  /** Legacy flat folder when team names are not provided. */
  gameName?: string;
  /** With team2Name, builds dated / `_nodate__` / `unknown_game__` segments (see `downloadS3Path`). */
  team1Name?: string;
  team2Name?: string;
  gameStartDateMs?: number | null;
  gameId?: string;
  customerId?: string;
  email?: string;
  section: string;
  row: string;
  seat: string;
  fileType: "ticket" | "image" | "gif";
  ticketTemplateName?: string;
}

/** Extract stadium name from game.stadium (string or populated object { name }) */
export const getStadiumName = (stadium: string | { name?: string } | undefined): string => {
  if (!stadium) return "Unknown_Stadium";
  if (typeof stadium === "object" && stadium?.name) return String(stadium.name);
  return String(stadium);
};

export const uploadImage = async ({
  imageUrl,
  action,
  stadium,
  gameName,
  team1Name,
  team2Name,
  gameStartDateMs,
  gameId,
  customerId,
  email,
  section,
  row,
  seat,
  fileType,
  ticketTemplateName,
}: UploadImageRequest): Promise<void> => {
  const stadiumStr = getStadiumName(stadium);
  console.log(`Logging ${fileType} ${action} to S3:`, { imageUrl, stadium: stadiumStr, gameName, section, row, seat });

  const rootFolder = action === "videoboard"
    ? "preapproved_videoboard_Images"
    : "downloaded_Images";

  const cleanedStadium = stadiumStr.replace(/[^a-zA-Z0-9_]/g, "_");
  const t1 = team1Name?.trim();
  const t2 = team2Name?.trim();
  let gameSegment: string;
  if (t1 && t2) {
    gameSegment = buildDownloadGameSegment({
      team1Name: t1,
      team2Name: t2,
      gameStartDateMs: gameStartDateMs ?? null,
      gameId: gameId ?? null,
    });
  } else if (gameName && String(gameName).trim()) {
    gameSegment = String(gameName).replace(/[^a-zA-Z0-9_]/g, "_");
  } else {
    gameSegment = buildDownloadGameSegment({ gameId: gameId ?? null });
  }
  const cleanedTemplateName = ticketTemplateName
    ? ticketTemplateName.replace(/[^a-zA-Z0-9_]/g, "_")
    : "Unknown_Ticket_Template";

  // Create filename with timestamp to ensure uniqueness.
  // Tokens must not contain `_` — operator Single Image Per Seat parses section/row/seat on `_`.
  const timestamp = Date.now();
  const fileExtension = fileType === "gif" ? "gif" : "png";
  const safeSection = sanitizeDownloadSeatToken(String(section || ""), "UnknownSection");
  const safeRow = sanitizeDownloadSeatToken(String(row || ""), "UnknownRow");
  const safeSeat = sanitizeDownloadSeatToken(String(seat || ""), "UnknownSeat");
  const filename = `${fileType}__${safeSection}_${safeRow}_${safeSeat}_${timestamp}.${fileExtension}`;

  let fileTypeFolder = "image";
  if (fileType === "ticket") {
    fileTypeFolder = `ticket/${cleanedTemplateName}`;
  } else if (fileType === "gif") {
    fileTypeFolder = "gif";
  }

  // Path structure (same layout for downloads vs videoboard; root folder differs):
  // {root}/Stadium/Game/image/filename
  // {root}/Stadium/Game/ticket/TicketTemplateName/filename
  // {root}/Stadium/Game/gif/filename
  const s3Key = `${rootFolder}/${cleanedStadium}/${gameSegment}/${fileTypeFolder}/${filename}`;

  console.log("Generated S3 key:", s3Key);

  try {
    const fileBlob = await fetch(imageUrl).then((res) => res.blob());
    const contentType = fileType === "gif" ? "image/gif" : "image/png";
    const { fields, url } = await getPreSignedUpload({
      contentType,
      s3Key,
    });

    const formData = new FormData();
    formData.append("key", fields.key);
    formData.append("acl", fields.acl);
    formData.append("Content-Type", fields["Content-Type"]);
    formData.append("bucket", fields.bucket);
    formData.append("X-Amz-Algorithm", fields["X-Amz-Algorithm"]);
    formData.append("X-Amz-Credential", fields["X-Amz-Credential"]);
    formData.append("X-Amz-Date", fields["X-Amz-Date"]);
    formData.append("X-Amz-Security-Token", fields["X-Amz-Security-Token"]);
    formData.append("Policy", fields.Policy);
    formData.append("X-Amz-Signature", fields["X-Amz-Signature"]);
    formData.append("file", fileBlob);

    // Use fetch for S3 presigned POST - axios instance adds Authorization header which S3 rejects
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    }

    console.log("uploadImageRequest() - upload response: ", response.status);

    if ((action === "download" || action === "videoboard") && gameId && (customerId || email)) {
      try {
        await instance.post(`${PATH.API}/${PATH.IMAGE}/${PATH.TRACK_ACTION}`, {
          customerId,
          email: customerId ? undefined : email,
          gameId,
          section: safeSection,
          row: safeRow,
          seat: safeSeat,
          action,
          s3Key,
        });
      } catch (trackErr) {
        console.warn("track-action failed, continuing upload flow:", trackErr);
      }
    }
  } catch (err) {
    console.error(`Error uploading ${fileType} ${action} to S3:`, err);
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
